import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { userApi, vacationApi, remoteApi } from '../utils/api';

import {
  Palmtree, Laptop, Clock, TrendingUp, Plus, Calendar,
  Sparkles, ChevronRight, CheckCircle, AlertCircle, XCircle, Loader
} from 'lucide-react';
import { Button } from '../components/ui/button';

const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  const cls = { pending: 'status-pending', approved: 'status-approved', rejected: 'status-rejected', cancelled: 'status-cancelled' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls[status] || ''}`}>{t(status)}</span>;
};

const BalanceRing = ({ used, total, color, label }) => {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const r = 28;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-16 h-16">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={r} fill="none" stroke="currentColor" strokeWidth="5" className="text-muted/30" />
          <circle cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="5"
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
            className="transition-all duration-700" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold">{total - used}</span>
        </div>
      </div>
      <span className="text-xs text-muted-foreground text-center leading-tight">{label}</span>
    </div>
  );
};

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [vacations, setVacations] = useState([]);
  const [remotes, setRemotes] = useState([]);
  const [monthlyRemote, setMonthlyRemote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [balRes, vacRes, remRes, monthRes] = await Promise.all([
        userApi.getBalance(user.id),
        vacationApi.list({ status: 'pending' }),
        remoteApi.list({ status: 'pending' }),
        remoteApi.monthlyBalance(),
      ]);
      setBalance(balRes.data);
      setVacations(vacRes.data.slice(0, 3));
      setRemotes(remRes.data.slice(0, 3));
      setMonthlyRemote(monthRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader className="h-6 w-6 animate-spin text-brand" />
    </div>
  );

  const vacUsed = balance?.vacation_used || 0;
  const vacPending = balance?.vacation_pending || 0;
  const vacTotal = balance?.vacation_total || 24;
  const vacAvail = vacTotal - vacUsed - vacPending;

  const remUsed = monthlyRemote ? monthlyRemote.full_days_used ?? monthlyRemote.used : (balance?.remote_used || 0);
  const remPending = monthlyRemote ? 0 : (balance?.remote_pending || 0);
  const remTotal = 6;
  const remAvail = monthlyRemote ? (monthlyRemote.full_days_available ?? monthlyRemote.available) : Math.max(0, remTotal - remUsed - remPending);
  const remHalfUsed = monthlyRemote?.half_days_used ?? 0;
  const remHalfAvail = monthlyRemote?.half_days_available ?? null;

  const carryover = balance?.carryover_days || 0;
  const carryoverUsed = balance?.carryover_used || 0;
  const carryoverAvail = carryover - carryoverUsed;

  const recentAll = [
    ...vacations.map(v => ({ ...v, kind: 'vacation' })),
    ...remotes.map(r => ({ ...r, kind: 'remote' }))
  ].sort((a, b) => b.created_at?.localeCompare(a.created_at)).slice(0, 5);

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-heading">
          Hola, {user?.first_name} 
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          {new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* Balance cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Vacaciones */}
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-testid="card-vacation-balance">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <Palmtree className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <span className="font-semibold font-heading text-sm">{t('vacation')}</span>
            </div>
            <BalanceRing used={vacUsed + vacPending} total={vacTotal} color="#00a651" label="libres" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Usados</span>
              <span className="font-medium">{vacUsed} días</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Pendiente</span>
              <span className="font-medium text-yellow-600">{vacPending} días</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-brand rounded-full transition-all duration-700" style={{ width: `${(vacUsed / vacTotal) * 100}%` }} />
            </div>
            <div className="flex justify-between text-xs font-semibold">
              <span className="text-brand">{vacAvail} {t('available')}</span>
              <span className="text-muted-foreground">{t('total')} {vacTotal}</span>
            </div>
          </div>
        </div>

        {/* Teletrabajo */}
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-testid="card-remote-balance">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Laptop className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="font-semibold font-heading text-sm">{t('remoteWork')}</span>
            </div>
            <BalanceRing used={remUsed + remPending} total={remTotal} color="#0ea5e9" label="libres" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Días completos este mes</span>
              <span className="font-medium">{remUsed} de 6</span>
            </div>
            {remHalfUsed > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Medias jornadas este mes</span>
                <span className="font-medium text-purple-600">{remHalfUsed} de 8</span>
              </div>
            )}
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-telework rounded-full transition-all duration-700" style={{ width: `${(remUsed / 6) * 100}%` }} />
            </div>
            {remHalfUsed > 0 && (
              <div className="h-1 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-purple-400 rounded-full transition-all duration-700" style={{ width: `${(remHalfUsed / 8) * 100}%` }} />
              </div>
            )}
            <div className="flex justify-between text-xs font-semibold">
              <span className="text-telework">{remAvail} {t('available')}</span>
              <span className="text-muted-foreground">{t('total')} 6/mes</span>
            </div>
          </div>
        </div>

        {/* Arrastrado */}
        {carryover > 0 && (
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-testid="card-carryover-balance">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                <TrendingUp className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              </div>
              <span className="font-semibold font-heading text-sm">Días arrastrados</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Total arrastrado</span>
                <span className="font-medium">{carryover} días</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Usados</span>
                <span className="font-medium">{carryoverUsed} días</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-orange-400 rounded-full" style={{ width: `${(carryoverUsed / carryover) * 100}%` }} />
              </div>
              <div className="flex justify-between text-xs font-semibold">
                <span className="text-orange-600">{carryoverAvail} disponibles</span>
                <span className="text-muted-foreground text-xs">hasta 30 abr</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick actions + recent requests */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick actions */}
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
          <h2 className="font-semibold font-heading text-sm mb-4">{t('quickActions')}</h2>
          <div className="space-y-2">
            <button
              data-testid="quick-new-vacation"
              onClick={() => {
                if (!user?.fecha_inicio) {
                  alert('Tu puesto y calendario no están formalizados (falta fecha de inicio). Contacta con Admón. de Personal.');
                  return;
                }
                navigate('/vacations/new');
              }}
              title={!user?.fecha_inicio ? 'Sin fecha de inicio formalizada. Contacta con Admón. de Personal.' : undefined}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors group ${
                !user?.fecha_inicio
                  ? 'bg-slate-100 dark:bg-slate-800/40 cursor-not-allowed opacity-60'
                  : 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <Palmtree className={`h-4 w-4 ${!user?.fecha_inicio ? 'text-slate-400' : 'text-green-600 dark:text-green-400'}`} />
                <span className={`text-sm font-medium ${!user?.fecha_inicio ? 'text-slate-500' : 'text-green-800 dark:text-green-300'}`}>{t('newVacation')}</span>
              </div>
              <ChevronRight className={`h-4 w-4 transition-colors ${!user?.fecha_inicio ? 'text-slate-300' : 'text-green-600/50 group-hover:text-green-600'}`} />
            </button>
            <button
              data-testid="quick-new-remote"
              onClick={() => navigate('/remote/new')}
              className="w-full flex items-center justify-between p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors group"
            >
              <div className="flex items-center gap-2.5">
                <Laptop className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-800 dark:text-blue-300">{t('newRemote')}</span>
              </div>
              <ChevronRight className="h-4 w-4 text-blue-600/50 group-hover:text-blue-600 transition-colors" />
            </button>
            <button
              data-testid="quick-calendar"
              onClick={() => navigate('/calendar')}
              className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
            >
              <div className="flex items-center gap-2.5">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('calendar')}</span>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
            </button>
            {/* Informe IA deshabilitado temporalmente (hilo "Flex - Teletrabajo duda") — código intacto para reactivar:
            <button
              data-testid="quick-ai-report"
              onClick={() => navigate('/ai-report')}
              className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
            >
              <div className="flex items-center gap-2.5">
                <Sparkles className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('aiReport')}</span>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
            </button>
            */}
          </div>
        </div>

        {/* Recent requests */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold font-heading text-sm">{t('recentRequests')}</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/my-requests')} className="text-xs text-brand hover:text-brand-hover">
              Ver todas <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </div>

          {recentAll.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Clock className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">No hay solicitudes recientes</p>
              <p className="text-xs mt-1">Usa las acciones rápidas para crear una nueva</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentAll.map((req) => (
                <div key={req.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${req.kind === 'vacation' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                      {req.kind === 'vacation'
                        ? <Palmtree className="h-4 w-4 text-green-600 dark:text-green-400" />
                        : <Laptop className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      }
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {req.kind === 'vacation' ? t('vacation') : t('remoteWork')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {req.kind === 'vacation'
                          ? `${req.start_date} → ${req.end_date} (${req.working_days}d)`
                          : `${req.date} · ${req.period_type === 'morning' ? 'Mañana' : req.period_type === 'afternoon' ? 'Tarde' : 'Día completo'}`
                        }
                      </p>
                    </div>
                  </div>
                  <StatusBadge status={req.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
